/**
 * Receipts captured on the phone, kept on the phone until they are sent.
 *
 * At the restaurant table the connection is often bad, and an installed web app on iOS gets no
 * background sync. So a capture goes into IndexedDB first and is sent whenever the app is open
 * with a connection: right away, when the connection returns, or the next time the app opens.
 *
 * Sending happens in two steps (create the document, then read it). The document id is saved
 * after the first, so a connection lost in between never creates the same receipt twice.
 */
import type { ScanPage } from "@/lib/api/documents";

export type CaptureKind = "KA" | "ER";

export type QueuedCapture = {
  id: string;
  workspaceId: string;
  kind: CaptureKind;
  /** The photos, in page order. The IndexedDB store keeps their bytes, not the files. */
  pages: File[];
  /**
   * The scan made on the phone (lib/scan/photo.ts): the PDF and how each photo became its page.
   * Absent when the phone could not decode a photo; services/ai scans those as before.
   */
  scan?: { pdf: File; pages: ScanPage[] };
  category?: string;
  deductiblePercent?: number;
  /** For a business meal: whom and why (the Finanzamt asks). */
  notes?: string;
  tip?: number;
  createdAt: string;
  /** Set once the document exists; from then on only reading it is left. */
  documentId?: string;
  attempts: number;
  /** The last error that was not a lost connection. */
  error?: string;
};

export type CaptureStore = {
  list: () => Promise<QueuedCapture[]>;
  put: (item: QueuedCapture) => Promise<void>;
  remove: (id: string) => Promise<void>;
};

export type SendOutcome = {
  documentId: string;
  docNumber?: string;
  /** False when the document was saved but could not be read. */
  extracted: boolean;
  grossTotal?: number;
  partnerName?: string;
  confidence?: number;
  warnings: string[];
  /** Why it could not be read, when it could not. */
  error?: string;
};

/** A failed fetch, as browsers report it: TypeError "Failed to fetch" / "Load failed". */
export function isConnectionError(error: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return error instanceof TypeError && /fetch|load failed|network/i.test(error.message);
}

/**
 * Sends what is waiting, oldest first. A lost connection stops the run and keeps everything for
 * later; any other error is noted on that receipt, which is kept and retried next time, and the
 * run goes on with the next one.
 */
export async function flushQueue(
  store: CaptureStore,
  send: (item: QueuedCapture) => Promise<SendOutcome>,
  onSent?: (item: QueuedCapture, outcome: SendOutcome) => void,
): Promise<{ sent: number; waiting: number }> {
  const items = (await store.list()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  let sent = 0;
  for (const [index, item] of items.entries()) {
    // Its photos could not be read back (see fromStored): nothing to send, it waits to be discarded.
    if (item.pages.length === 0) continue;
    try {
      const outcome = await send(item);
      await store.remove(item.id);
      sent += 1;
      onSent?.(item, outcome);
    } catch (error) {
      if (isConnectionError(error)) return { sent, waiting: items.length - index };
      await store.put({
        ...item,
        attempts: item.attempts + 1,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return { sent, waiting: items.length - sent };
}

export function memoryStore(initial: QueuedCapture[] = []): CaptureStore {
  const items = new Map(initial.map((item) => [item.id, item]));
  return {
    list: async () => [...items.values()],
    put: async (item) => {
      items.set(item.id, item);
    },
    remove: async (id) => {
      items.delete(id);
    },
  };
}

/** A photo as IndexedDB keeps it: its bytes, never the File itself. */
type StoredPage = { name: string; type: string; lastModified: number; data: ArrayBuffer };
type StoredCapture = Omit<QueuedCapture, "pages" | "scan"> & {
  pages: (StoredPage | Blob)[];
  scan?: { pdf: StoredPage; pages: ScanPage[] };
};

/**
 * Safari cannot be trusted with a File in IndexedDB: WebKit refuses to store one ("Error preparing
 * Blob/File data"), and iOS stores it but hands back a file whose upload arrives cut off
 * (PocketBase: "Failed to read the submitted data", multipart EOF). So the store keeps each photo's
 * bytes and builds fresh files from them when the queue is read.
 */
export async function toStoredPage(file: File): Promise<StoredPage> {
  return {
    name: file.name,
    type: file.type,
    lastModified: file.lastModified,
    data: await file.arrayBuffer(),
  };
}

export async function fromStoredPage(page: StoredPage | Blob, index: number): Promise<File> {
  // Queued before the store kept bytes: read the old file once, while it is still readable.
  if (page instanceof Blob) {
    const name = page instanceof File ? page.name : `photo-${index + 1}.jpg`;
    return new File([await page.arrayBuffer()], name, { type: page.type });
  }
  return new File([page.data], page.name, { type: page.type, lastModified: page.lastModified });
}

/** Turns a stored entry back into a capture; a photo that can no longer be read is said so. */
export async function fromStored(stored: StoredCapture): Promise<QueuedCapture> {
  try {
    const pages = await Promise.all(stored.pages.map(fromStoredPage));
    const scan = stored.scan && {
      pdf: await fromStoredPage(stored.scan.pdf, 0),
      pages: stored.scan.pages,
    };
    return { ...stored, pages, scan };
  } catch {
    return { ...stored, pages: [], scan: undefined, error: "unreadable" };
  }
}

/** The real store. Not unit tested (jsdom has no IndexedDB); e2e covers it offline and online. */
export function indexedDbStore(name = "presto-capture"): CaptureStore {
  const open = () =>
    new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(name, 1);
      request.onupgradeneeded = () =>
        request.result.createObjectStore("captures", { keyPath: "id" });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  const run = async <T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest,
  ) => {
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        const request = action(db.transaction("captures", mode).objectStore("captures"));
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error);
      });
    } finally {
      db.close();
    }
  };
  return {
    list: async () => {
      const stored = await run<StoredCapture[]>("readonly", (store) => store.getAll());
      return Promise.all(stored.map(fromStored));
    },
    put: async (item) => {
      // Read the files before the transaction: it closes at the first await that is not its own.
      const pages = await Promise.all(item.pages.map(toStoredPage));
      const scan = item.scan && { pdf: await toStoredPage(item.scan.pdf), pages: item.scan.pages };
      await run("readwrite", (store) => store.put({ ...item, pages, scan }));
    },
    remove: async (id) => {
      await run("readwrite", (store) => store.delete(id));
    },
  };
}
